// web-commands.ts — the PER-APP half of the web shape: maps oriora_knowcode's Tauri commands
// (normally served by ipc.rs/db.rs/walker.rs over native SQLite + a filesystem walker) to the
// shared browser WebStore. Plain CRUD is a few lines each. The code-indexing / retrieval commands
// are NATIVE-ONLY (a browser can't walk an arbitrary local folder or run the FTS index), so on web
// they return valid-but-EMPTY shells; a faithful web build would source code some other way.

import type { CommandHandler, WebStore } from "./web-runtime";
import { indexFiles, splitIdentifiers, searchChunks, findSymbolsIn } from "./knowcode-web";
import type { PickedFile, StoredChunk, WebSymbolRow } from "./knowcode-web";
import { browserSearch, browserFetch } from "./web-search-browser";

const REPOS = "repos";
const FILES = "files";
const SYMBOLS = "symbols";
const HISTORY = "history";
const CHUNKS = "chunks"; // web-only: the chunk index (native uses FTS5 chunk_fts)

function uuid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Repos (CRUD) ──
    list_repos: () => db.all(REPOS),
    get_repo: ({ id }) => db.get(REPOS, String(id)),
    repo_count: () => db.all(REPOS).length,
    delete_repo: ({ id }) => {
      // Native FK-cascade wipes the whole index; mirror it.
      for (const t of [FILES, SYMBOLS, CHUNKS]) {
        for (const r of db.all(t).filter((x) => String(x.repo_id) === String(id))) db.remove(t, String(r.id));
      }
      db.remove(REPOS, String(id));
      return null;
    },

    // ── Files / symbols (CRUD reads; populated only by native indexing) ──
    list_files: ({ repoId }) => db.all(FILES).filter((r) => (r as { repo_id?: string }).repo_id === String(repoId)),
    all_symbols: ({ repoId }) => db.all(SYMBOLS).filter((r) => (r as { repo_id?: string }).repo_id === String(repoId)),

    // ── Indexing + retrieval — 1:1 ports (knowcode-web.ts). The browser can't walk a
    // PATH, so App.tsx hands the user-picked files ({ path, text }) in the `files` arg;
    // same skip rules + ceilings as walker.rs. replace_index semantics mirrored (upsert
    // on root, wipe + reinsert). NB: web invoke shim passes args VERBATIM (camelCase).
    index_folder: ({ root, name, files }) => {
      const rootStr = String(root ?? "");
      const picked = (Array.isArray(files) ? files : []).map((f) => ({
        path: String((f as { path?: unknown }).path ?? ""),
        text: String((f as { text?: unknown }).text ?? ""),
      })) as PickedFile[];
      const result = indexFiles(picked);
      const now = nowIso();
      const trimmed = String(name ?? "").trim() || "Untitled repo";
      const byteCount = result.files.reduce((a, f) => a + f.byte_count, 0);
      const existing = db.all(REPOS).find((r) => String(r.root) === rootStr);
      let repoId: string, createdAt: string;
      if (existing) {
        repoId = String(existing.id);
        createdAt = String(existing.created_at);
        for (const t of [FILES, SYMBOLS, CHUNKS]) {
          for (const r of db.all(t).filter((x) => String(x.repo_id) === repoId)) db.remove(t, String(r.id));
        }
        db.update(REPOS, repoId, {
          name: trimmed, file_count: result.files.length, chunk_count: result.chunks.length,
          symbol_count: result.symbols.length, byte_count: byteCount, updated_at: now,
        });
      } else {
        repoId = uuid();
        createdAt = now;
        db.insert(REPOS, {
          id: repoId, name: trimmed, root: rootStr, file_count: result.files.length,
          chunk_count: result.chunks.length, symbol_count: result.symbols.length,
          byte_count: byteCount, created_at: now, updated_at: now,
        });
      }
      for (const f of result.files) {
        db.insert(FILES, { id: uuid(), repo_id: repoId, path: f.path, lang: f.lang, line_count: f.line_count, byte_count: f.byte_count });
      }
      for (const c of result.chunks) {
        db.insert(CHUNKS, { id: uuid(), repo_id: repoId, path: c.path, lang: c.lang, start_line: c.start_line, end_line: c.end_line, text: c.text, shadow: splitIdentifiers(c.text) });
      }
      for (const [kind, symName, path, line, lang] of result.symbols) {
        db.insert(SYMBOLS, { id: uuid(), repo_id: repoId, kind, name: symName, path, line, lang });
      }
      return {
        repo: {
          id: repoId, name: trimmed, root: rootStr,
          file_count: result.files.length, chunk_count: result.chunks.length,
          symbol_count: result.symbols.length, byte_count: byteCount,
          created_at: createdAt, updated_at: now,
        },
        skipped: result.skipped,
      };
    },
    language_breakdown: ({ repoId }) => {
      const counts = new Map<string, number>();
      for (const f of db.all(FILES).filter((r) => String(r.repo_id) === String(repoId))) {
        counts.set(String(f.lang), (counts.get(String(f.lang)) ?? 0) + 1);
      }
      return [...counts.entries()].sort((a, b) => b[1] - a[1]);
    },
    search_code: ({ repoId, query, limit }) => {
      const cap = Math.min(Math.max(Number(limit ?? 12) || 12, 1), 24);
      const stored = db.all(CHUNKS)
        .filter((r) => String(r.repo_id) === String(repoId))
        .map((r) => ({
          path: String(r.path), lang: String(r.lang), start_line: Number(r.start_line),
          end_line: Number(r.end_line), text: String(r.text), shadow: String(r.shadow ?? ""),
        })) as StoredChunk[];
      return searchChunks(String(repoId), stored, String(query ?? ""), cap);
    },
    find_symbols: ({ repoId, query, limit }) => {
      const cap = Math.min(Math.max(Number(limit ?? 40) || 40, 1), 200);
      const rows = db.all(SYMBOLS)
        .filter((r) => String(r.repo_id) === String(repoId))
        .map((r) => ({
          id: String(r.id), repo_id: String(r.repo_id), kind: String(r.kind), name: String(r.name),
          path: String(r.path), line: Number(r.line), lang: String(r.lang),
        })) as WebSymbolRow[];
      return findSymbolsIn(rows, String(query ?? ""), cap);
    },

    // ── History (CRUD) ──
    add_history: ({ repoId, repoName, question, answer, citations }) => {
      const row = {
        id: uuid(),
        repo_id: String(repoId),
        repo_name: String(repoName),
        question: String(question),
        answer: String(answer),
        citations: JSON.stringify(citations ?? []),
        created_at: nowIso(),
      };
      return db.insert(HISTORY, row);
    },
    list_history: () =>
      db
        .all(HISTORY)
        .sort((a, b) => String((b as { created_at?: string }).created_at ?? "").localeCompare(String((a as { created_at?: string }).created_at ?? ""))),
    delete_history: ({ id }) => {
      db.remove(HISTORY, String(id));
      return null;
    },
    clear_history: () => {
      db.all(HISTORY).forEach((r) => db.remove(HISTORY, r.id));
      return null;
    },
  };
}
